import { useState } from "react";
import { Landmark, X } from "lucide-react";

const INDIAN_BANKS = [
  "ICICI Bank", "HDFC Bank", "State Bank of India", "Axis Bank", "Kotak Mahindra Bank",
  "Bank of Baroda", "Punjab National Bank", "Union Bank of India", "Canara Bank",
  "IndusInd Bank", "IDFC First Bank", "Yes Bank", "IDBI Bank", "Federal Bank",
  "Bank of India", "Central Bank of India", "Bank of Maharashtra", "RBL Bank",
];

/**
 * Ask / edit which bank + account a statement belongs to.
 * mode="prompt" is shown right after an upload where the bank could not be detected.
 * mode="edit" is the pencil on an already-imported statement.
 */
export function BankInfoDialog({ statement, mode = "edit", onSave, onClose }) {
  const [bank, setBank] = useState(statement?.bank_name && statement.bank_name !== "Unknown Bank" ? statement.bank_name : "");
  const [acct, setAcct] = useState(statement?.account_number || "");
  const [nickname, setNickname] = useState(statement?.nickname || "");
  const [saving, setSaving] = useState(false);
  const isPrompt = mode === "prompt";

  async function submit() {
    const finalBank = bank.trim() || statement?.bank_name || "Unknown Bank";
    if (!finalBank && !nickname.trim()) return;
    setSaving(true);
    await onSave({ bank_name: finalBank, account_number: acct.trim(), nickname: nickname.trim() });
    setSaving(false);
  }

  const previewLabel = nickname.trim() || (acct.trim() ? `${bank || "Bank"} ••${acct.trim().slice(-4)}` : (bank || "Bank"));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="bank-info-dialog">
      <div className="bg-white border border-[#E5E3DB] w-full max-w-md shadow-xl">
        <div className="flex items-start justify-between p-5 border-b border-[#E5E3DB]">
          <div className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-[#C84B31]" />
            <div>
              <div className="font-headings text-lg font-bold text-[#1A1A18]">
                {isPrompt ? "Which bank is this statement for?" : "Edit bank & account"}
              </div>
              <div className="text-xs text-[#68665E] mt-0.5 max-w-[300px] truncate" title={statement?.filename}>
                {statement?.filename}
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="bank-info-close"><X className="w-4 h-4 text-[#68665E]" /></button>
        </div>

        <div className="p-5 space-y-4">
          {isPrompt && (
            <p className="text-sm text-[#68665E]">
              We couldn't read the bank name from this file. Tell us which bank &amp; account it is —
              we'll label its transactions and save it as an account for expenses.
            </p>
          )}
          <div>
            <label className="block text-xs uppercase tracking-widest text-[#68665E] font-semibold mb-1.5">Bank name</label>
            <input
              list="bank-name-options"
              value={bank}
              onChange={(e) => setBank(e.target.value)}
              placeholder="e.g. ICICI Bank"
              className="input-field w-full"
              data-testid="bank-info-name"
              autoFocus
            />
            <datalist id="bank-name-options">
              {INDIAN_BANKS.map((b) => <option key={b} value={b} />)}
            </datalist>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-[#68665E] font-semibold mb-1.5">
              Account number <span className="normal-case tracking-normal text-[#A09E96]">(optional — keeps multiple accounts in one bank separate)</span>
            </label>
            <input
              value={acct}
              onChange={(e) => setAcct(e.target.value)}
              placeholder="e.g. 25XXXXXX6351"
              className="input-field w-full"
              data-testid="bank-info-account"
            />
            {acct.trim() && (
              <p className="text-xs text-[#68665E] mt-1.5">
                Account number stored; last 4 = <span className="font-semibold text-[#1A1A18]">{acct.trim().slice(-4)}</span>
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-[#68665E] font-semibold mb-1.5">
              Nickname <span className="normal-case tracking-normal text-[#A09E96]">(optional — shows everywhere instead of the bank name)</span>
            </label>
            <input
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="e.g. ICICI Current – Andheri"
              className="input-field w-full"
              data-testid="bank-info-nickname"
            />
            <p className="text-xs text-[#68665E] mt-1.5">
              Will show as <span className="font-semibold text-[#1A1A18]" data-testid="bank-info-preview">{previewLabel}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-[#E5E3DB]">
          <button onClick={onClose} className="btn-secondary" data-testid="bank-info-cancel">
            {isPrompt ? "Skip for now" : "Cancel"}
          </button>
          <button onClick={submit} disabled={(!bank.trim() && !nickname.trim()) || saving} className="btn-primary disabled:opacity-50" data-testid="bank-info-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
